<template>
  <div class="tenant-dashboard">
    <Breadcrumbs main="Dashboard" title="Overview" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="dashboard-glass dashboard-welcome">
            <h5 class="mb-0">Welcome back, {{ userName }}</h5>
            <p class="mb-0 text-muted small">Firm activity at a glance.</p>
          </div>
        </div>
      </div>
      <div class="stat-widgets">
        <div v-for="stat in stats" :key="stat.title" class="stat-widget dashboard-glass">
          <div class="stat-widget__header">
            <span class="stat-widget__title">{{ stat.title }}</span>
            <vue-feather :type="stat.icon" class="stat-widget__icon" />
          </div>
          <div class="stat-widget__value">{{ stat.primary }}</div>
          <p v-for="(detail, index) in stat.details" :key="index" class="stat-widget__detail mb-0">
            {{ detail }}
          </p>
        </div>
      </div>
      <div class="row g-3">
        <div class="col-xl-8">
          <div class="dashboard-glass dashboard-panel">
            <div class="dashboard-panel__header">
              <h6 class="mb-0">Recent Activity</h6>
            </div>
            <div class="dashboard-panel__body activity-scroll">
              <div class="activity">
                <div v-for="item in activity" :key="item.title" class="d-flex mb-3">
                  <div class="gradient-round m-r-30 gradient-line-1">
                    <vue-feather :type="item.icon" />
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="small">{{ item.title }} <span class="pull-right f-12">{{ item.time }}</span></h6>
                    <p class="mb-0 small text-muted">{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-4">
          <div class="dashboard-glass dashboard-panel">
            <div class="dashboard-panel__header">
              <h6 class="mb-0">Quick Links</h6>
            </div>
            <div class="dashboard-panel__body">
              <ul class="list-group list-group-flush">
                <li v-for="link in quickLinks" :key="link.title" class="list-group-item px-0 border-0 bg-transparent">
                  <router-link :to="link.path" class="d-flex align-items-center small">
                    <vue-feather :type="link.icon" class="me-2" size="16" />
                    {{ link.title }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getDashboardStats } from '@/services/statService'

export default {
  computed: {
    ...mapGetters('auth', ['currentUser']),
    userName() {
      return this.currentUser?.fullName || 'User'
    },
  },
  data() {
    return {
      stats: [
        { title: 'Clients', primary: '—', details: ['— team members'], icon: 'users' },
        { title: 'Matters', primary: '—', details: ['— open · — closed'], icon: 'briefcase' },
        { title: 'Bills', primary: '—', details: ['— outstanding', '— total bills'], icon: 'file-text' },
        { title: 'Invoices', primary: '—', details: ['— outstanding'], icon: 'clipboard' },
        { title: 'Court Diary', primary: '—', details: ['Upcoming in 30 days'], icon: 'calendar' },
      ],
      activity: [],
      quickLinks: [
        { title: 'Clients', path: '/administration/clients', icon: 'users' },
        { title: 'Matters', path: '/court/matters', icon: 'briefcase' },
        { title: 'Documents', path: '/court/documents', icon: 'folder' },
        { title: 'Billing', path: '/accounts/billing', icon: 'credit-card' },
      ],
    }
  },

  created() {
    this.loadStats()
  },

  methods: {
    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    async loadStats() {
      try {
        const data = await getDashboardStats()

        this.stats[0].primary = String(data.clients ?? 0)
        this.stats[0].details = [`${data.users ?? 0} team members`]

        this.stats[1].primary = String((data.openMatters ?? 0) + (data.closedMatters ?? 0))
        this.stats[1].details = [`${data.openMatters ?? 0} open · ${data.closedMatters ?? 0} closed`]

        this.stats[2].primary = String(data.pendingBills ?? 0)
        this.stats[2].details = [
          `${this.formatMoney(data.totalPendingBills)} outstanding`,
          `${data.bills ?? 0} total bills`,
        ]

        this.stats[3].primary = String(data.pendingInvoices ?? 0)
        this.stats[3].details = [`${this.formatMoney(data.totalPendingInvoices)} outstanding`]

        this.stats[4].primary = String(data.upcomingEvents30Days ?? 0)
        this.stats[4].details = ['Upcoming in 30 days']

        this.activity = [
          {
            title: 'Dashboard updated',
            time: 'Now',
            description: `${data.openMatters ?? 0} open matters, ${data.pendingBills ?? 0} pending bills, ${data.upcomingEvents30Days ?? 0} upcoming events.`,
            icon: 'check-circle',
          },
        ]
      } catch {
        this.activity = [
          {
            title: 'Could not load statistics',
            time: 'Now',
            description: 'Check that the API is running and you are signed in.',
            icon: 'alert-circle',
          },
        ]
      }
    },
  },
}
</script>

<style scoped lang="scss">
.tenant-dashboard {
  --glass-bg: rgba(255, 255, 255, 0.42);
  --glass-border: rgba(255, 255, 255, 0.65);
  --glass-shadow: 0 4px 20px rgba(68, 102, 242, 0.07);
}

.dashboard-glass {
  background: var(--glass-bg);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  box-shadow: var(--glass-shadow);
}

.dashboard-welcome {
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.stat-widgets {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.stat-widget {
  padding: 0.7rem 0.85rem;
  min-height: 0;
  background: linear-gradient(135deg, rgba(34, 175, 71, 0.22), rgba(46, 204, 113, 0.18));
  border-color: rgba(34, 175, 71, 0.28);
  box-shadow: 0 4px 16px rgba(34, 175, 71, 0.12);
}

.stat-widget__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.stat-widget__title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(24, 120, 52, 0.85);
}

.stat-widget__icon {
  width: 15px;
  height: 15px;
  color: #1a8f3d;
  flex-shrink: 0;
}

.stat-widget__value {
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 0.2rem;
  color: #157a34;
}

.stat-widget__detail {
  font-size: 0.72rem;
  color: rgba(24, 120, 52, 0.72);
  line-height: 1.35;
}

.dashboard-panel__header {
  padding: 0.65rem 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}

.dashboard-panel__body {
  padding: 0.75rem 0.9rem;
}

@media (max-width: 1399px) {
  .stat-widgets {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .stat-widgets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 479px) {
  .stat-widgets {
    grid-template-columns: 1fr;
  }
}
</style>
